import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../utils/axiosConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [authState, setAuthState] = useState({
        isAuthenticated: false,
        userRole: undefined,
        accountType: undefined,
        hasTeamId: false
    });

    const api = axios;

    // Check authentication status with enhanced logging
    const checkAuth = async () => {
        try {
            const storedToken = localStorage.getItem('token');
            if (!storedToken) {
                setLoading(false);
                return;
            }

            setToken(storedToken);
            
            const response = await api.get('/api/auth/me');
            console.log('Auth response:', response.data);
            
            if (response.data.success) {
                const userData = {
                    ...response.data.user,
                    accountType: response.data.user.role,
                    isUsingSharedCredits: response.data.user.role === 'individual' && response.data.user.team_leader_id,
                    credits: response.data.user.isUsingSharedCredits 
                        ? response.data.user.teamLeader?.credits 
                        : response.data.user.credits
                };
                setUser(userData);
            } else {
                console.log('Auth check failed:', response.data);
                localStorage.removeItem('token');
                setToken(null);
                setUser(null);
            }
        } catch (error) {
            console.error('Auth check error:', error.response?.data || error);
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    // Login with enhanced user data handling
    const login = async (email, password) => {
        try {
            console.log('Login attempt:', { email, password });
            
            const response = await api.post('/api/auth/login', {
                email,
                password
            });

            console.log('Login response:', response.data);

            if (response.data.success) {
                const { token: newToken, user: userData } = response.data;
                localStorage.setItem('token', newToken);
                setToken(newToken);
                
                const enhancedUser = {
                    ...userData,
                    accountType: userData.role,
                    isUsingSharedCredits: userData.role === 'individual' && userData.team_leader_id,
                    credits: userData.credits || 3
                };
                setUser(enhancedUser);
                
                setAuthState({
                    isAuthenticated: true,
                    userRole: userData.role,
                    accountType: userData.account_type,
                    hasTeamId: !!userData.team_id
                });

                return { success: true };
            }
            return { success: false, message: response.data.message };
        } catch (error) {
            console.error('Login error:', error);
            console.error('CORS or Network Error:', {
                origin: window.location.origin,
                target: api.defaults.baseURL
            });
            return { 
                success: false, 
                message: error.response?.data?.message || 'Login failed'
            };
        }
    };

    // Logout with token cleanup
    const logout = async () => {
        try {
            if (token) {
                // Try to call logout endpoint, but don't wait for it
                try {
                    await api.post('/api/auth/logout');
                } catch (error) {
                    // Log but don't block logout on server error
                    console.warn('Server logout notification failed:', error);
                }
            }

            // Always clear local state
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            
            return { success: true };
        } catch (error) {
            console.error('Logout error:', error);
            // Still clear local state even if server call fails
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            
            return { 
                success: false, 
                message: 'Error during logout' 
            };
        }
    };

    // Initialize auth check on mount
    useEffect(() => {
        console.log('AuthProvider mounted, checking auth...'); // Added debug log
        checkAuth();
    }, []);

    // Refresh user data with enhanced logging
    const refreshUserData = async () => {
        try {
            const response = await api.get('/api/users/me');
            console.log('Refresh user response:', response.data); // Debug log
            if (response.data.success) {
                const userData = {
                    ...response.data.user,
                    isUsingSharedCredits: response.data.user.role === 'individual' && response.data.user.team_leader_id,
                    credits: response.data.user.isUsingSharedCredits 
                        ? response.data.user.teamLeader?.credits 
                        : response.data.user.credits
                };
                console.log('Updated user data:', userData); // Debug log
                setUser(userData);
            }
        } catch (error) {
            console.error('Error refreshing user data:', error.response?.data || error);
        }
    };

    // Update the register function to remove credits handling
    const register = async (userData) => {
        try {
            setLoading(true);
            setError(null);
            
            const registrationData = {
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                password: userData.password,
                accountType: userData.accountType.toLowerCase(),
                ...(userData.accountType === 'school_admin' && {
                    schoolName: userData.schoolName,
                    districtName: userData.districtName
                })
            };
            
            const response = await api.post('/api/auth/register', registrationData);
            
            if (response.data.success) {
                const { token: newToken, user } = response.data;
                
                localStorage.setItem('token', newToken);
                setToken(newToken);
                
                // Remove credits from enhancedUser
                const enhancedUser = {
                    ...user,
                    accountType: user.role
                };
                setUser(enhancedUser);
                
                return { success: true, user: enhancedUser };
            }
            
            throw new Error(response.data.message || 'Registration failed');
            
        } catch (error) {
            console.error('Registration error:', error.response?.data || error);
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.errors?.[0]?.msg || 
                               'Registration failed';
            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    const getAvailableCredits = () => {
        if (!user) return 0;
        return user.isUsingSharedCredits && user.teamLeader 
            ? user.teamLeader.credits 
            : user.credits;
    };

    const value = {
        user,
        token,
        loading,
        error,
        login,
        logout,
        checkAuth,
        isAuthenticated: !!user,
        isCorporate: user?.role === 'corporate',
        isTeamLeader: user?.role === 'team_leader',
        refreshUserData,
        register,
        getAvailableCredits,
        isUsingSharedCredits: user?.isUsingSharedCredits
    };

    console.log('Current auth state:', { // Added debug log
        isAuthenticated: !!user,
        userRole: user?.role,
        accountType: user?.accountType,
        hasTeamId: !!user?.team_id
    });

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};